import React, { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Check, X, ChevronDown, ChevronUp } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { PendingApprovalSummary } from '@/features/chat/services/chatApi'

interface ApprovalQueueCardProps {
  approval: PendingApprovalSummary
  onDecide: (conversationId: string, action: 'approved' | 'rejected') => void
  deciding: boolean
}

// One row per conversation the graph is paused on, waiting for a sensitive-
// action decision (see chatApi.ts's PendingApprovalSummary doc comment).
// Approve/Reject here is the same lightweight decision the Dashboard's Today
// panel already offers — not the full edit-arguments flow, which stays
// exclusive to the live in-chat ApprovalCard while you're actually watching
// the conversation stream.
export const ApprovalQueueCard: React.FC<ApprovalQueueCardProps> = ({ approval, onDecide, deciding }) => {
  const { currentTheme: t } = useTheme()
  const [expanded, setExpanded] = useState(false)
  const [firstTool, ...restTools] = approval.tools

  return (
    <div
      className="rounded-2xl p-4 flex flex-col gap-3"
      style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[13.5px] font-semibold font-mono truncate" style={{ color: t.text }}>
              {firstTool?.name ?? 'action'}
            </p>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold shrink-0"
              style={{ backgroundColor: t.warningBackground, color: t.warningText }}
            >
              Needs approval
            </span>
          </div>
          <p className="text-[12px] mt-0.5" style={{ color: t.mutedText }}>
            {restTools.length > 0 ? `+${restTools.length} more · ` : ''}
            {formatDistanceToNow(new Date(approval.requestedAt), { addSuffix: true })}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 self-start text-[12px] font-semibold"
        style={{ color: t.accent }}
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {expanded ? 'Hide parameters' : 'View parameters'}
      </button>

      {expanded && (
        <div className="flex flex-col gap-2.5">
          {approval.tools.map((tool) => (
            <div key={tool.toolCallId}>
              <p className="text-[11px] font-mono font-semibold mb-1" style={{ color: t.secondrytext }}>{tool.name}</p>
              <pre
                className="text-[11px] font-mono p-3 rounded-xl overflow-x-auto"
                style={{ backgroundColor: t.activeBackground, color: t.text }}
              >
                {JSON.stringify(tool.arguments, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={deciding}
          onClick={() => onDecide(approval.conversationId, 'rejected')}
          className="flex flex-1 items-center justify-center gap-1.5 h-9 rounded-xl text-[12.5px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: t.errorBackground, color: t.errorText }}
        >
          <X size={12} />
          Reject
        </button>
        <button
          type="button"
          disabled={deciding}
          onClick={() => onDecide(approval.conversationId, 'approved')}
          className="flex flex-1 items-center justify-center gap-1.5 h-9 rounded-xl text-[12.5px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: t.accentGradient }}
        >
          <Check size={12} />
          Approve
        </button>
      </div>
    </div>
  )
}
